import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import classes from './physicianMedicationReport.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import { useQuery } from '../../../../utils/hooks/useQuery';
import MonthPicker from '../../../_common/Form/DatePickers/MonthPicker/MonthPicker';
import Loader from '../../../_common/Loader/Loader';
import Api from '../../../../api/api';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import Modal from '../../../_common/Modal/Modal';
import YearPicker from '../../../_common/Form/DatePickers/YearPicker/YearPicker';
import RangeQuarterPicker from '../../../_common/Form/DatePickers/QuarterPicker/RangeQuarterPicker';
import PhysicianMedicationTable from './PhysicianMedicationTable';

const types = [
  {
    title: 'Revenue',
    key: 'type',
    value: 'revenue',
  },
  {
    title: 'Quantity',
    key: 'type',
    value: 'quantity',
  },
];

const dateTypes = [
  {
    title: 'Month',
    key: 'dateType',
    value: 'single',
  },
  {
    title: 'Quarter',
    key: 'dateType',
    value: 'quarter',
  },
  {
    title: 'Year',
    key: 'dateType',
    value: 'year',
  },
];

const renderCompareDate = (filters) => {
  if (filters.dateType === 'single') {
    return moment(filters.startDate).format('MMMM yyyy');
  }

  if (filters.dateType === 'quarter') {
    const compareDates = _.compact([filters.compareStartDate, filters.compareEndDate]);

    return `${_.compact([filters.startDate, filters.endDate]).map((date) => moment(date).format('YYYY [Q]Q')).join(' - ')} ${compareDates.length
      ? `/ ${compareDates.map((date) => moment(date).format('YYYY [Q]Q')).join(' - ')}` : ''}`;
  }

  return `${_.compact([filters.startDate, filters.compareStartDate]).map((date) => moment(date).format('YYYY')).join(' / ')}`;
};

const thisMonth = moment().format('yyyy-MM');

const PhysicianMedicationReport = ({ mode = 'by_physician' }) => {
  const navigate = useNavigate();

  const { query, queryToStringify, setQuery } = useQuery();

  const firstLoad = useRef(true);
  const timeout = useRef();

  const [reportData, setReportData] = useState([]);

  const [physicians, setPhysicians] = useState([]);
  const [medications, setMedications] = useState([]);

  const [searchValues, setSearchValues] = useState({
    physicianName: query.physicianName || '',
    medicationName: query.medicationName || '',
  });

  const [searchLoading, setSearchLoading] = useState('');

  const [filters, setFilters] = useState({
    physicianId: query.physicianId || '',
    medicationId: query.medicationId || '',
    type: query.type || 'revenue',
    dateType: query.dateType || 'single',
    startDate: query.startDate ? moment(query.startDate).format('yyyy-MM') : thisMonth,
    endDate: query.endDate || '',
    compareStartDate: query.compareStartDate || '',
    compareEndDate: query.compareEndDate || '',
  });

  const [compareModalInfo, setCompareModalInfo] = useState({});

  const [loading, loadingToggle] = useState(true);

  useLayoutEffect(() => {
    firstLoad.current = true;
  }, [mode]);

  useEffect(() => {
    (async () => {
      loadingToggle(true);
      const sendingData = {
        physicianId: filters.physicianId,
        medicationId: filters.medicationId,
        type: filters.type,
        dateType: filters.dateType,
        mode,
      };

      if (sendingData.dateType === 'single') {
        sendingData.date1 = filters.startDate;
      } else if (sendingData.dateType === 'quarter') {
        sendingData.date1 = _.compact([filters.startDate, filters.endDate]);
        sendingData.date2 = _.compact([filters.compareStartDate, filters.compareEndDate]);
      } else if (sendingData.dateType === 'year') {
        sendingData.date1 = _.compact([filters.startDate]);
        sendingData.date2 = _.compact([filters.compareStartDate]);
      }

      const { data } = await Api.getPhysicianMedication(sendingData);

      setReportData(data.data);

      setQuery({
        ...filters,
        physicianName: searchValues.physicianName || '',
        medicationName: searchValues.medicationName || '',
      });

      firstLoad.current = false;
      loadingToggle(false);
    })();
  }, [filters]);

  const changeFilterData = (path, value) => {
    setFilters((prev) => {
      if (path === 'physicianId') {
        setSearchValues((prevSearch) => ({ ...prevSearch, physicianName: value.firstName ? `${value.firstName} ${value.lastName}` : '' }));

        return { ...prev, [path]: value.id };
      }

      if (path === 'medicationId') {
        setSearchValues((prevSearch) => ({ ...prevSearch, medicationName: value.name || '' }));

        return { ...prev, [path]: value.id };
      }

      return { ...prev, [path]: value };
    });
  };

  const changeModalData = (path, value) => {
    setCompareModalInfo((prev) => {
      if (path === 'quarter') {
        const [startDate, endDate] = value;

        return { ...prev, startDate, endDate };
      }

      if (path === 'quarterCompare') {
        const [compareStartDate, compareEndDate] = value;

        return { ...prev, compareStartDate, compareEndDate };
      }

      return { ...prev, [path]: value };
    });
  };

  const compare = () => {
    setFilters((prev) => {
      if (compareModalInfo.dateType === 'single') {
        return {
          ...prev,
          dateType: compareModalInfo.dateType,
          startDate: compareModalInfo.startDate,
          endDate: '',
          compareStartDate: '',
          compareEndDate: '',
        };
      }

      if (compareModalInfo.dateType === 'quarter') {
        return {
          ...prev,
          dateType: compareModalInfo.dateType,
          startDate: compareModalInfo.startDate
            ? moment(moment(compareModalInfo.startDate, 'yyyy-MM').format('YYYY [Q]Q'), 'YYYY [Q]Q').format('yyyy-MM')
            : null,
          endDate: compareModalInfo.endDate
            ? moment(moment(compareModalInfo.endDate, 'yyyy-MM').format('YYYY [Q]Q'), 'YYYY [Q]Q').add(2, 'month').format('yyyy-MM')
            : null,
          compareStartDate: compareModalInfo.compareStartDate
            ? moment(moment(compareModalInfo.compareStartDate, 'yyyy-MM').format('YYYY [Q]Q'), 'YYYY [Q]Q').format('yyyy-MM')
            : null,
          compareEndDate: compareModalInfo.compareEndDate
            ? moment(moment(compareModalInfo.compareEndDate, 'yyyy-MM').format('YYYY [Q]Q'), 'YYYY [Q]Q').add(2, 'month').format('yyyy-MM')
            : null,
        };
      }

      return {
        ...prev,
        dateType: compareModalInfo.dateType,
        startDate: compareModalInfo.startDate,
        endDate: '',
        compareStartDate: compareModalInfo.compareStartDate,
        compareEndDate: '',
      };
    });

    setCompareModalInfo({});
  };

  const selectSearch = (path, value) => {
    setSearchValues((prev) => ({ ...prev, [path]: value }));

    clearTimeout(timeout.current);

    if (value.trim()) {
      setSearchLoading(path);

      timeout.current = setTimeout(async () => {
        if (path === 'physicianName') {
          const { data: { data } } = await Api.getPhysicians({
            name: value,
            limit: 100,
          });

          setPhysicians(data.response);
        } else {
          const { data: { data } } = await Api.getProducts({
            name: value,
            limit: 100,
          });

          setMedications(data.response);
        }

        setSearchLoading('');
      }, 500);
    } else {
      setPhysicians([]);
      setSearchLoading('');
    }
  };

  const navigateTo = async (data) => {
    const newQuery = mode === 'by_physician'
      ? {
        ...query,
        physicianId: '',
        physicianName: '',
        medicationId: data.ProductId,
        medicationName: data.medicationName,
      }

      : {
        ...query,
        physicianId: data.PhysicianId,
        physicianName: data.physicianName,
        medicationId: '',
        medicationName: '',
      };

    navigate(
      `/dashboard/${mode === 'by_physician' ? 'medications' : 'physicians'}-report${queryToStringify(newQuery)}`,
      { replace: true },
    );

    setFilters(newQuery);
    setSearchValues({
      physicianName: newQuery.physicianName,
      medicationName: newQuery.medicationName,
    });
  };

  const tableHeader = useMemo(() => {
    let header = [
      {
        name: mode === 'by_physician' ? 'Medication' : 'Physician',
        path: `${(mode === 'by_physician' ? 'medication' : 'physician')}Name`,
      },
    ];

    const dates = _.map(reportData?.[0]?.date, (value, date) => ({ value, date }));

    if (query.dateType === 'single') {
      header = [...header, ...dates.map((r, index) => (
        {
          type: 'report',
          name: moment(r.date, 'yyyy-MM').format('MMMM yyyy'),
          path: `date.${r.date}`,
          comparePath: index ? `date.${dates[index - 1].date}` : '',
        }
      ))];
    } else if (query.dateType === 'quarter') {
      header = [...header, ...dates.map((r, index) => (
        {
          type: 'report',
          name: !index
            ? `${_.compact([query.startDate, query.endDate]).map((date) => moment(date).format('YYYY [Q]Q')).join(' - ')}`
            : `${_.compact([query.compareStartDate, query.compareEndDate]).map((date) => moment(date).format('YYYY [Q]Q')).join(' - ')}`,
          path: `date.${r.date}`,
          comparePath: index ? `date.${dates[index - 1].date}` : '',
        }
      ))];
    } else {
      header = [...header, ...dates.map((r, index) => (
        {
          type: 'report',
          name: moment(r.date, 'yyyy-MM').format('yyyy'),
          path: `date.${r.date}`,
          comparePath: index ? `date.${dates[index - 1].date}` : '',
        }
      ))];
    }

    return header;
  }, [query, reportData, mode]);

  return (
    <div className={classes.wrapper}>
      <div className={classes.filters_wrapper}>
        <div className={`${classes.left_filters} ${mode === 'by_physician' ? classes.order1 : classes.order2}`}>
          <SelectWithSearch
            label="Physician"
            onChange={(value) => selectSearch('physicianName', value)}
            loading={searchLoading === 'physicianName'}
            options={physicians}
            onSelectOption={(option, value) => changeFilterData('physicianId', option, value)}
            searchValue={searchValues.physicianName}
            optionPathname="fullName"
            optionKey=""
            hideClearButton={mode === 'by_physician'}
            zIndex={31}
          />

          <SelectWithSearch
            label="Medication"
            onChange={(value) => selectSearch('medicationName', value)}
            loading={searchLoading === 'medicationName'}
            options={medications}
            onSelectOption={(option, value) => changeFilterData('medicationId', option, value)}
            searchValue={searchValues.medicationName}
            optionPathname="name"
            optionKey=""
            hideClearButton={mode === 'by_product'}
            zIndex={31}
          />

          <div className={classes.button_filter_wrapper}>
            <FilterButton
              onClick={changeFilterData}
              activeValue={filters.type}
              buttonsData={types}
            />
          </div>
        </div>

        <div className={classes.compare_wrapper}>
          <div className={classes.compare_date}>
            {renderCompareDate(filters)}
          </div>

          <div className={classes.compare_select}>
            <Button
              title="Compare"
              reverseColor
              onClick={() => setCompareModalInfo(filters)}
            />
          </div>
        </div>
      </div>

      <div className={classes.table_wrapper}>
        {firstLoad.current
          ? <Loader />

          : (
            <PhysicianMedicationTable
              data={reportData}
              header={tableHeader}
              loading={loading}
              onClick={navigateTo}
              type={query.type}
            />
          )}
      </div>

      <Modal
        open={!_.isEmpty(compareModalInfo)}
        onClose={() => setCompareModalInfo({})}
        className={classes.compare_modal}
      >
        <div className={classes.compare_modal_title}>
          <h2>Compare</h2>

          <FilterButton
            onClick={changeModalData}
            activeValue={compareModalInfo.dateType}
            buttonsData={dateTypes}
          />
        </div>

        <div className={classes.compare_fields}>
          <div className={classes.month_picker_wrapper}>
            {compareModalInfo.dateType === 'single'
              ? (
                <div className={classes.month_picker_month_wrapper}>
                  <MonthPicker
                    date={compareModalInfo.startDate}
                    hideClearButton
                    size="default"
                    onChange={(m) => changeModalData('startDate', m)}
                  />
                </div>
              )

              : compareModalInfo.dateType === 'quarter'
                ? (
                  <div className={classes.month_picker_quarter_wrapper}>
                    <RangeQuarterPicker
                      startDate={compareModalInfo.startDate}
                      endDate={compareModalInfo.endDate}
                      hideClearButton
                      size="default"
                      onChange={(m) => changeModalData('quarter', m)}
                    />

                    {' - '}

                    <RangeQuarterPicker
                      startDate={compareModalInfo.compareStartDate}
                      endDate={compareModalInfo.compareEndDate}
                      hideClearButton
                      size="default"
                      onChange={(m) => changeModalData('quarterCompare', m)}
                    />
                  </div>
                )

                : (
                  <div className={classes.month_picker_year_wrapper}>
                    <YearPicker
                      date={compareModalInfo.startDate}
                      hideClearButton
                      excludeDate={compareModalInfo.compareStartDate}
                      size="default"
                      onChange={(m) => changeModalData('startDate', m)}
                    />

                    {' - '}

                    <YearPicker
                      date={compareModalInfo.compareStartDate}
                      hideClearButton
                      excludeDate={compareModalInfo.startDate}
                      size="default"
                      onChange={(m) => changeModalData('compareStartDate', m)}
                    />
                  </div>
                )}
          </div>
        </div>

        <div className={classes.compare_btns}>
          <Button
            title="Cancel"
            clearButton
            onClick={() => setCompareModalInfo({})}
          />

          <Button
            title="Compare"
            reverseColor
            onClick={compare}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PhysicianMedicationReport;
